import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Paper, Typography, useMediaQuery, CircularProgress } from '@material-ui/core';
import L from 'leaflet';
import config from 'src/config';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { renderToString } from 'react-dom/server';
import { Help } from '@material-ui/icons';
import 'leaflet/dist/leaflet.css';
import 'leaflet-side-by-side';

import { LayersPlus, MapMarkerPlus } from 'src/utils/mdIcons';
import {
  getCombinedBounds,
  getMarkerIcon,
  createComparisonLayerControl,
  createSliderManager,
  removeComparisonControls,
  cleanupMapControls,
  getLeafletInfo,
} from 'src/components/map/mapHelper';
import { objectIsEmpty } from 'src/utils/util';
import { useTitilerApi } from 'src/services';
import { formatNumber } from 'src/utils/formatters';


const useStyles = makeStyles(() => ({
  map: {
    position: 'relative',
    width: '100%',
    height: ({ height }) => `${height}px`,
  },
}));

const { satelliteStreetsBaseMap } = config;

const addTools = map => {
  const drawnItems = new L.FeatureGroup();
  map.addLayer(drawnItems);
  const drawControl = new L.Control.Draw({
    position: 'topleft',
    draw: {
      rectangle: false,
      circlemarker: false,
      polyline: false,
      polygon: true,
      circle: false,
      marker: {
        icon: getMarkerIcon(5),
        repeatMode: true,
      },
    },
  });
  map.addControl(drawControl);

  const iconStyle = { color: '#464646' };

  const kmlImportPointsToolButton = L.easyButton({
    id: 'import-points-btn',
    position: 'topleft',
    type: 'replace',
    states: [ {
      title: 'Importar puntos (KML/KMZ)',
      icon: renderToString(<MapMarkerPlus fontSize="small" style={iconStyle} />),
    } ],
  });
  kmlImportPointsToolButton.addTo(map);

  const kmlImportAreaToolButton = L.easyButton({
    id: 'import-area',
    position: 'topleft',
    type: 'replace',
    states: [ {
      title: 'Importar área de interés (KML/KMZ)',
      icon: renderToString(<LayersPlus fontSize="small" style={iconStyle} />),
    } ],
  });
  kmlImportAreaToolButton.addTo(map);

  const helpButton = L.easyButton({
    id: 'help-btn',
    position: 'topleft',
    type: 'replace',
    states: [ {
      title: 'Ayuda',
      icon: renderToString(<Help fontSize="small" style={iconStyle} />),
    } ],
  });
  helpButton.addTo(map);
};

const MapWrapper = props => {

  const { id = 'map', className, options, layers = [], legend, showTools = false, disableZoom = false, height = 100,
    events, mapRef: ref, fitBoundsOnUpdate = false, comp = {}, box, bounds, showPixelValue = false } = props;

  const titilerApi = useTitilerApi();

  const classes = useStyles({ height });
  const theme = useTheme();
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'));

  const mapRef = useMemo(() => ref || {}, [ ref ]);

  const prevLayersRef = useRef([]);

  const sliderRef = useRef(null);
  const sliderLayersRef = useRef({ left: [], right: [] });
  const pixelInfoControlRef = useRef(null);
  const [ pixelInfo, setPixelInfo ] = useState({ lat: 0, lng: 0, value: 'N/D', isLoading: false });
  const isCrosshairModeRef = useRef(false);

  // Event listeners para detectar Command (Mac) o Ctrl izquierdo (Windows)
  const handleKeyDown = e => {
    if (e.metaKey || (e.ctrlKey && e.location === KeyboardEvent.DOM_KEY_LOCATION_LEFT)) {
      const mapContainer = mapRef.current?.getContainer();
      if (mapContainer) {
        mapContainer.style.cursor = 'crosshair';
      }
      isCrosshairModeRef.current = true;
    }
  };

  const handleKeyUp = e => {
    if (!e.metaKey && !e.ctrlKey) {
      const mapContainer = mapRef.current?.getContainer();
      if (mapContainer) {
        mapContainer.style.cursor = 'pointer';
      }
      isCrosshairModeRef.current = false;
    }
  };

  const defaultOptions = {
    center: [ -33.4679997, -70.7003513 ],
    zoom: 5,
    layers: [
      L.tileLayer(satelliteStreetsBaseMap, {}),
    ],
  };

  const PixelInfoDisplay = () =>
    <Paper elevation={0}>
      <Box p={0.5} width={280} display="flex" justifyContent="center">
        <Typography variant="caption">
          <Box display="flex" alignItems="center">
            Lat: {formatNumber(pixelInfo.lat, 6)} &nbsp;|&nbsp; Lon: {formatNumber(pixelInfo.lng, 6)} &nbsp;|&nbsp; Valor:
            <Box width={24} ml={0.5}>{pixelInfo.isLoading ? <CircularProgress size={12} thickness={4.8} /> : pixelInfo.value}</Box>
          </Box>
        </Typography>
      </Box>
    </Paper>;

  const getActiveSide = mouseX => {
    const map = mapRef.current;
    if (!map) {
      return 'left';
    }

    const hasLeftLayer = sliderLayersRef.current.left.some(layer => map.hasLayer(layer));
    const hasRightLayer = sliderLayersRef.current.right.some(layer => map.hasLayer(layer));

    if (hasLeftLayer && !hasRightLayer) {
      return 'left';
    }
    if (hasRightLayer && !hasLeftLayer) {
      return 'right';
    }

    if (!sliderRef.current || !sliderRef.current._range) {
      return 'left';
    }
    const mapContainer = map.getContainer();
    const mapWidth = mapContainer.offsetWidth;
    const sliderPosition = sliderRef.current._range.value;
    const dividerX = mapWidth * sliderPosition;
    return mouseX <= dividerX ? 'left' : 'right';
  };

  const isPointInRasterBounds = (lat, lng) => {
    if (!comp || !comp.bounds) {
      return true;
    }
    const { west, south, east, north } = comp.bounds;
    const leafletBounds = [ [ south, west ], [ north, east ] ];
    const bounds = L.latLngBounds(leafletBounds);
    return bounds.contains([ lat, lng ]);
  };

  const updatePixelValue = (lat, lng, value = null, isLoading = false) => {
    setPixelInfo(prevInfo => ({
      lat,
      lng,
      value: value !== null ? value : prevInfo.value,
      isLoading,
    }));
  };

  const fetchPixelValue = async (lat, lng, activeSide) => {
    updatePixelValue(lat, lng, null, true);

    try {
      const filePath = activeSide === 'left' || !comp.right ? comp.left.path : comp.right.path;
      const result = await titilerApi.getPixelValue({ filePath, lon: lng, lat });
      const rawValue = result?.values?.[0];
      const formattedValue = (rawValue === null || rawValue === undefined) ? 'N/D' : formatNumber(rawValue, 1);
      updatePixelValue(lat, lng, formattedValue, false);
    } catch (error) {
      console.warn('Error obteniendo valores de píxel:', error);
      updatePixelValue(lat, lng, 'N/D', false);
    }
  };

  useEffect(() => {
    mapRef.current = L.map(id, { ...defaultOptions, ...options });
    const map = mapRef.current;

    // Configurar cursores personalizados para el mapa
    const mapContainer = map.getContainer();
    mapContainer.style.cursor = 'pointer';
    map.on('mousedown', () => !isCrosshairModeRef.current && (mapContainer.style.cursor = 'grab'));
    map.on('mouseup', () => !isCrosshairModeRef.current && (mapContainer.style.cursor = 'pointer'));
    map.on('dragstart', () => mapContainer.style.cursor = 'grabbing');
    map.on('dragend', () => mapContainer.style.cursor = 'pointer');

    if (showTools) {
      addTools(map);
    }

    if (events) {
      const eventsObj = events(map);
      Object.keys(eventsObj).forEach(key => map.on(key, () => eventsObj[key]()));
    }

    if (disableZoom && map.zoomControl) {
      map.zoomControl.disable();
    }

    if (box) {
      const boxBounds = L.geoJSON(box).getBounds();
      const zoomComplete = map.getBoundsZoom(boxBounds, true);
      map.setMaxBounds(boxBounds);
      map.setMinZoom(zoomComplete);
    }

    if (bounds) {
      map.fitBounds(bounds);
    }

    return () => {
      if (pixelInfoControlRef.current) {
        map.removeControl(pixelInfoControlRef.current);
      }
      cleanupMapControls(map);
      map.remove();
    };
  }, [ ]);  // eslint-disable-line

  useEffect(() => {
    if (!showPixelValue) {
      return;
    }
    const map = mapRef.current;
    if (showPixelValue) {
      pixelInfoControlRef.current = getLeafletInfo(PixelInfoDisplay);
      pixelInfoControlRef.current.addTo(map);
    }

    const handleMapClick = e => {
      if (showPixelValue && comp.display && comp.left?.path) {
        const { lat, lng } = e.latlng;
        const mouseX = e.containerPoint.x;
        const activeSide = comp.right ? getActiveSide(mouseX) : 'left';

        if (isPointInRasterBounds(lat, lng, activeSide)) {
          fetchPixelValue(lat, lng, activeSide);
        } else {
          updatePixelValue(lat, lng, 'N/A', false);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    map.on('click', handleMapClick);
    return () => {
      if (pixelInfoControlRef.current) {
        map.removeControl(pixelInfoControlRef.current);
      }
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
      map.off('click', handleMapClick);
    };
  }, [ comp, showPixelValue ]); // eslint-disable-line

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !options?.maxZoom) {
      return;
    }
    // Actualizar maxZoom del mapa y sus capas
    map.setMaxZoom(options.maxZoom);
    map.eachLayer(layer => {
      if (layer instanceof L.TileLayer && layer.options) {
        layer.options.maxZoom = options.maxZoom;
      }
    });
  }, [ options?.maxZoom ]); // eslint-disable-line

  useEffect(() => {
    const map = mapRef.current;
    if (sliderRef.current) {
      sliderRef.current.remove();
    }

    sliderLayersRef.current.left.forEach(layer => map.removeLayer(layer));
    sliderLayersRef.current.right.forEach(layer => map.removeLayer(layer));

    if (comp.display && comp.left && comp.right) {
      const left = [];
      if (comp.left.indicator) {
        left.push(comp.left.indicator);
      }
      const right = [];
      if (comp.right.indicator) {
        right.push(comp.right.indicator);
      }
      left.forEach(layer => layer.addTo(map));
      right.forEach(layer => layer.addTo(map));
      sliderLayersRef.current.left = left;
      sliderLayersRef.current.right = right;
      sliderRef.current = L.control.sideBySide(left, right).addTo(map);

      const hasLayerControl = createComparisonLayerControl(map, comp);
      if (hasLayerControl) {
        createSliderManager(map, comp, left, right, sliderRef);
      }
    } else if (comp.display && comp.left && !comp.right) {
      const left = [];
      if (comp.left.indicator) {
        left.push(comp.left.indicator);
      }
      left.forEach(layer => layer.addTo(map));
      sliderLayersRef.current.left = left;
      sliderLayersRef.current.right = [];
    } else {
      removeComparisonControls(map);
    }

    // eslint-disable-next-line
  }, [ comp, showPixelValue ]);

  useEffect(() => {
    const map = mapRef.current;
    prevLayersRef.current.forEach(layer => {
      if (map.hasLayer(layer)) {
        map.removeLayer(layer);
      }
    });

    if (layers.length > 0) {
      layers.forEach(layer => {
        layer.addTo(map);
      });
      prevLayersRef.current = layers;
    } else {
      prevLayersRef.current = [];
    }

    if (map._currentLegend) {
      map._currentLegend.remove();
      map._currentLegend = null;
    }

    if (legend) {
      legend.addTo(map);
      map._currentLegend = legend;
    }

    if (fitBoundsOnUpdate && layers.length > 0) {
      const currentBounds = bounds ? bounds : getCombinedBounds(layers);
      if (!objectIsEmpty(currentBounds)) {
        map.fitBounds(currentBounds);
      }
    }
  // eslint-disable-next-line
  }, [ layers, legend, fitBoundsOnUpdate]);

  useEffect(() => {
    if (isDownSm && mapRef.current.zoomControl) {
      mapRef.current.removeControl(mapRef.current.zoomControl);
    }
  }, [ mapRef, isDownSm ]);

  useEffect(() => {
    if (showPixelValue && pixelInfoControlRef.current) {
      const map = mapRef.current;
      map.removeControl(pixelInfoControlRef.current);
      pixelInfoControlRef.current = getLeafletInfo(PixelInfoDisplay);
      pixelInfoControlRef.current.addTo(map);
    }
  }, [ pixelInfo, showPixelValue ]); // eslint-disable-line

  return (
    <div id={id} className={clsx(classes.map, className)} />
  );
};

MapWrapper.propTypes = {
  className: PropTypes.string,
  options: PropTypes.object,
  id: PropTypes.string,
  layers: PropTypes.array,
  legend: PropTypes.object,
  showTools: PropTypes.bool,
  disableZoom: PropTypes.bool,
  fitBoundsOnUpdate: PropTypes.bool,
  height: PropTypes.number,
  events: PropTypes.func,
  mapRef: PropTypes.object,
  comp: PropTypes.object,
  box: PropTypes.object,
  bounds: PropTypes.object,
  showPixelValue: PropTypes.bool,
};


export { MapWrapper };
